import { getMetaErrorMessage, readMetaErrorCode, readMetaRetryAfterSeconds } from './metaAdsConfig';
import { normalizeMetaAd, normalizeMetaCampaign } from './metaCampaigns';
import { isDeletedMetaCampaign } from './metaDeletedCampaigns';
import { stripMetaTerrioMark } from './metaFormat';
import { readMetaConnectionWrite, readMetaWriteBlock } from './metaWriteBlock';

export { readMetaConnectionWrite, readMetaWriteBlock };

/*
 * F2a + F2b · Acciones en Meta desde Terrio: pausar, reactivar, renombrar, presupuesto diario / total,
 * fecha de fin, archivar y eliminar, en campañas, conjuntos (dentro del detalle de la campaña) y
 * anuncios (sin presupuesto ni fecha). Contrato: argenlotapi `docs/frontend/FRONTEND_META_ESCRITURA.md`.
 * Todo depende del bloque `write` que manda la API (sin la bandera no viene → no se muestra
 * nada: PROD queda igual). El Front no deduce reglas: solo lee los flags.
 */

const bool = (v) => v === true;
const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);

/**
 * Motivos de `write.reason` con los que las acciones no se muestran (ni en gris). `archived` aparte:
 * lo archivado solo se puede eliminar (§7: `can_delete: true` con `reason: archived`).
 */
export const META_WRITE_HIDDEN_REASONS = ['blocked', 'kill_switch', 'not_connected', 'deleted'];
/** Motivos que el dueño resuelve en ⚙ (acciones en gris + tooltip que lleva a ⚙). */
export const META_WRITE_SETTINGS_REASONS = ['account_not_enabled', 'scope_missing'];

export const META_WRITE_OWNER_ONLY_TIP = 'Solo el dueño puede modificar campañas en Meta';
export const META_WRITE_NOT_ENABLED_TIP = 'Habilitá esta cuenta en Configuración de Meta';
export const META_WRITE_SCOPE_TIP = 'Falta el permiso de Meta. Revisalo en Configuración de Meta';
export const META_WRITE_PERMISSION_MESSAGE = 'Para hacer cambios, aceptá el permiso para administrar anuncios.';
export const META_WRITE_KILL_SWITCH_MESSAGE = 'No disponible por ahora.';
export const META_WRITE_RATE_LIMITED_TITLE = 'Meta pidió esperar un momento';
export const META_WRITE_BUDGET_LIMIT_TITLE = 'Ya cambiaste el presupuesto 4 veces en esta hora';
export const META_WRITE_UNCONFIRMED_MESSAGE = 'No pudimos confirmar el cambio.';

/** ¿Este entorno muestra algo de escritura? (bloque presente, disponible y no `prod_locked`). */
export function metaWriteVisible(connectionWrite) {
  return Boolean(connectionWrite && connectionWrite.available && connectionWrite.reason !== 'prod_locked');
}

/** Una fila de `getmetawritesettings` (§3.2). */
export function readMetaWriteAccount(raw) {
  const id = str(raw?.ad_account_id == null ? '' : String(raw.ad_account_id));
  if (!id) return null;
  return {
    id,
    name: str(raw.name) || `Cuenta ${id}`,
    currency: str(raw.currency)?.toUpperCase() || null,
    isTracked: bool(raw.is_tracked),
    enabled: bool(raw.enabled),
    writable: bool(raw.writable),
    canEnable: bool(raw.can_enable),
    blockedReason: str(raw.blocked_reason),
    realMoney: raw.real_money !== false,
  };
}

/** `data` de `getmetawritesettings` (o la respuesta de `updatemetawritesettings` para una fila). */
export function readMetaWriteSettings(res) {
  const data = res?.data && typeof res.data === 'object' ? res.data : {};
  return {
    viewerCanManage: bool(data.viewer_can_manage),
    killSwitch: bool(data.kill_switch),
    prodLocked: bool(data.prod_locked),
    connected: bool(data.connected),
    scopeGranted: bool(data.scope_granted),
    accounts: (Array.isArray(data.accounts) ? data.accounts : []).map(readMetaWriteAccount).filter(Boolean),
  };
}

/** Cómo se ve una cuenta en «Acciones en Meta» de ⚙. */
export function metaWriteAccountView(account) {
  if (!account) return null;
  if (['scope_missing', 'scope_not_for_account'].includes(account.blockedReason)) {
    return { kind: 'reconnect', tooltip: 'Falta el permiso de Meta' };
  }
  // Cuentas bloqueadas (denylist, no permitidas, etc.): «No disponible» + tooltip breve, sin explicar el porqué.
  if (account.blockedReason === 'not_tracked') return { kind: 'unavailable', tooltip: 'Primero seguila en Cuentas publicitarias' };
  if (account.blockedReason === 'not_connected') return { kind: 'unavailable', tooltip: 'Meta no está conectado' };
  if (account.blockedReason && !account.enabled) return { kind: 'unavailable', tooltip: 'No se puede modificar desde Terrio' };
  return {
    kind: 'switch',
    // Deshabilitar se puede siempre; habilitar, solo con `can_enable`.
    toggleable: account.enabled || account.canEnable,
    tooltip: account.enabled || account.canEnable ? null : 'No disponible por ahora',
  };
}

export const META_BUDGET_ACTIONS = ['set_daily_budget', 'set_lifetime_budget'];
/** Acciones que primero piden un valor (nombre, monto o fecha). */
export const META_WRITE_EDIT_ACTIONS = ['rename', ...META_BUDGET_ACTIONS, 'set_end_time'];

const CHILDREN = { campaign: 'sus conjuntos y anuncios', adset: 'sus anuncios' };

/**
 * Texto corto de cada aviso de la vista previa (la API manda textos largos; el Front usa los suyos
 * por `code` y cae al de la API solo si el código es nuevo). `null` = no se muestra.
 * `action` cambia el verbo (pausar / archivar) y el motivo del gasto (reactivar / presupuesto / fecha).
 * `action: 'create'` = crear campaña (F2b API, FRONTEND_META_CREAR.md §4.1–4.3).
 */
export function metaWriteWarningText(warning, level = 'campaign', action = null) {
  const children = CHILDREN[level] || null;
  switch (warning?.code) {
    case 'CASCADE':
      if (!children) return null;
      return action === 'archive' ? `También se archivan ${children}.` : `También se pausan ${children}.`;
    case 'REAL_MONEY':
      // Crear sale en pausa: que activarla gasta ya se entiende, y el aviso queda de más.
      if (action === 'create') return null;
      if (META_BUDGET_ACTIONS.includes(action)) return 'Puede gastar más plata real.';
      if (action === 'set_end_time') return 'Puede gastar durante más días.';
      return 'Volverá a gastar presupuesto.';
    case 'NO_PAYMENT_METHOD':
      if (action === 'create') return 'La cuenta no tiene medio de pago: Meta crea la campaña en pausa, pero no el anuncio.';
      return 'La cuenta no tiene medio de pago: no va a entregar.';
    // Crear campaña (F2b API).
    case 'CREATED_PAUSED': return 'Se crea en pausa: no gasta hasta que la actives.';
    case 'PAYMENT_UNKNOWN': return 'No pudimos saber si la cuenta tiene medio de pago.';
    case 'PAGE_UNVERIFIED': return 'Meta no confirmó que esta página pueda anunciar con la cuenta.';
    case 'TEXT_LONG': return 'El título o el texto son largos: se pueden ver recortados.';
    case 'IMAGE_SIZE': return 'La foto no tiene la proporción de Meta: se puede ver recortada.';
    case 'PARENT_PAUSED': return level === 'ad' ? 'La campaña está en pausa: no va a entregar.' : 'Está en pausa en otro nivel: no va a entregar.';
    case 'LEARNING_PHASE': return 'Un cambio grande puede reiniciar el aprendizaje de Meta.';
    case 'CBO_APPLIES_TO_ADSETS': return 'Meta lo reparte entre sus conjuntos.';
    case 'BUDGET_CHANGES_LEFT': {
      const m = /quedan?\s+(\d+)/i.exec(warning?.text || '');
      if (!m) return warning?.text || null;
      const n = Number(m[1]);
      return n === 1 ? 'Te queda 1 cambio de presupuesto en esta hora.' : `Te quedan ${n} cambios de presupuesto en esta hora.`;
    }
    case 'LIFETIME_PACING': return 'El total se reparte en otra cantidad de días.';
    case 'REACTIVATES': return 'Puede volver a entregar.';
    case 'ADSETS_OWN_END_TIME': return 'Estos conjuntos tienen su propia fecha y no cambian:';
    case 'ARCHIVE_IS_FINAL': return 'Después solo se puede eliminar.';
    case 'IRREVERSIBLE': return 'No se puede deshacer.';
    case 'CASCADE_DELETE': return children ? `También se eliminan ${children}.` : null;
    case 'ARCHIVE_INSTEAD': return 'Si solo lo querés sacar, archivalo.';
    case 'HISTORY_KEPT': return 'En Terrio quedan su historial y sus métricas.';
    default: return warning?.text || null;
  }
}

/** Avisos que van en rojo (eliminar). */
export const META_WRITE_DANGER_WARNINGS = ['IRREVERSIBLE', 'CASCADE_DELETE'];

/** Avisos de plata real / tope: el aviso unificado va en ámbar si hay alguno (nunca se pierden). */
export const META_WRITE_MONEY_WARNINGS = ['REAL_MONEY', 'NO_PAYMENT_METHOD', 'BUDGET_CHANGES_LEFT'];

const sentence = (t) => (t ? `${t.charAt(0).toUpperCase()}${t.slice(1)}` : '');
const lower = (t) => (t ? `${t.charAt(0).toLowerCase()}${t.slice(1)}` : '');
const noDot = (t) => String(t || '').replace(/[.:]\s*$/, '');

/**
 * UN solo aviso por vista previa (claridad visual: nada de cajas apiladas). Se arma con los `code`
 * que mandó la API (si falta uno, esa parte no se dice), en una o dos líneas:
 * - Archivar: «También se archivan sus conjuntos y anuncios. Después solo se puede eliminar; en Terrio
 *   quedan su historial y métricas.»
 * - Eliminar (rojo): «No se puede deshacer: también se eliminan sus conjuntos y anuncios. Si solo lo
 *   querés sacar, archivalo.» (`HISTORY_KEPT` no se repite acá: lo importante es el peligro).
 * - Presupuesto / fecha / reactivar: primero la plata real y los topes (ámbar), después el resto.
 * `tone`: `error` (eliminar) › `warn` (plata real / tope) › `info`. `null` = sin aviso.
 * @returns {{ tone: 'error'|'warn'|'info', text: string, codes: string[] }|null}
 */
export function metaWritePreviewNotice(preview, level = 'campaign', action = null) {
  const warnings = Array.isArray(preview?.warnings) ? preview.warnings : [];
  const codes = warnings.map((w) => w.code);
  const has = (code) => codes.includes(code);
  const text = (code) => {
    const w = warnings.find((x) => x.code === code);
    return w ? metaWriteWarningText(w, level, action) : null;
  };
  const used = new Set();
  const take = (code) => {
    const t = has(code) ? text(code) : null;
    if (t) used.add(code);
    return t;
  };
  const parts = [];
  if (action === 'delete') {
    const irreversible = take('IRREVERSIBLE');
    const cascade = take('CASCADE_DELETE');
    if (irreversible && cascade) parts.push(`${noDot(irreversible)}: ${lower(cascade)}`);
    else if (irreversible || cascade) parts.push(irreversible || cascade);
    const instead = take('ARCHIVE_INSTEAD');
    if (instead) parts.push(instead);
    if (has('HISTORY_KEPT')) used.add('HISTORY_KEPT');
  } else if (action === 'archive') {
    const cascade = take('CASCADE');
    if (cascade) parts.push(cascade);
    const final = take('ARCHIVE_IS_FINAL');
    const kept = has('HISTORY_KEPT') ? (used.add('HISTORY_KEPT'), 'en Terrio quedan su historial y métricas.') : null;
    if (final && kept) parts.push(`${noDot(final)}; ${kept}`);
    else if (final || kept) parts.push(sentence(final || kept));
  } else {
    // Plata real primero (con el aprendizaje si viene: «Puede gastar más plata real y reiniciar…»).
    const money = take('REAL_MONEY');
    const learning = has('LEARNING_PHASE') ? (used.add('LEARNING_PHASE'), 'puede reiniciar el aprendizaje de Meta.') : null;
    if (money && learning && META_BUDGET_ACTIONS.includes(action)) parts.push(`${noDot(money)} y reiniciar el aprendizaje de Meta.`);
    else {
      if (money) parts.push(money);
      if (learning) parts.push(sentence(`un cambio grande ${learning}`));
    }
    ['NO_PAYMENT_METHOD', 'PARENT_PAUSED', 'BUDGET_CHANGES_LEFT', 'CASCADE', 'REACTIVATES', 'CBO_APPLIES_TO_ADSETS', 'LIFETIME_PACING'].forEach((code) => {
      const t = take(code);
      if (t) parts.push(t);
    });
    const own = take('ADSETS_OWN_END_TIME');
    if (own) {
      const names = Array.isArray(preview?.adsets) ? preview.adsets : [];
      parts.push(names.length ? `${noDot(own)}: ${names.join(' · ')}.` : `${noDot(own)}.`);
    }
  }
  // Códigos nuevos (sin texto propio): el texto de la API, al final.
  warnings.forEach((w) => {
    if (used.has(w.code) || parts.length >= 6) return;
    const t = metaWriteWarningText(w, level, action);
    if (t && !['CASCADE', 'CASCADE_DELETE'].includes(w.code)) { used.add(w.code); parts.push(t); }
  });
  if (!parts.length) return null;
  let tone = 'info';
  if (codes.some((c) => META_WRITE_DANGER_WARNINGS.includes(c))) tone = 'error';
  else if (codes.some((c) => META_WRITE_MONEY_WARNINGS.includes(c))) tone = 'warn';
  return { tone, text: parts.map((p) => sentence(p.trim())).join(' '), codes: codes.filter((c) => used.has(c)) };
}

const NOUNS = {
  campaign: { noun: 'campaña', Noun: 'Campaña', the: 'La campaña', o: 'a' },
  adset: { noun: 'conjunto', Noun: 'Conjunto', the: 'El conjunto', o: 'o' },
  ad: { noun: 'anuncio', Noun: 'Anuncio', the: 'El anuncio', o: 'o' },
};
const nounOf = (level) => NOUNS[level] || NOUNS.campaign;

export function metaWriteActionLabel(level, action) {
  if (!NOUNS[level]) return '';
  const { noun } = nounOf(level);
  switch (action) {
    case 'pause': return `Pausar ${noun}`;
    case 'resume': return `Reactivar ${noun}`;
    case 'rename': return `Renombrar ${noun}`;
    case 'set_daily_budget': return level === 'ad' ? '' : 'Cambiar presupuesto diario';
    case 'set_lifetime_budget': return level === 'ad' ? '' : 'Cambiar presupuesto total';
    case 'budget': return level === 'ad' ? '' : 'Cambiar presupuesto';
    case 'set_end_time': return level === 'ad' ? '' : 'Cambiar fecha de fin';
    case 'archive': return `Archivar ${noun}`;
    case 'delete': return `Eliminar ${noun}`;
    default: return '';
  }
}

/**
 * Presupuesto de una campaña (`campaign.budget`, solo si vive en la campaña) o de un conjunto
 * (`readMetaAdSetBudget`): `{ budgetType: 'daily'|'lifetime'|null, amount, currency, endsAt }`.
 * Sin monto cargado → `budgetType: null` (no se ofrece cambiarlo: Meta no deja pasar de un tipo a otro).
 */
export function metaWriteBudgetOf(level, obj, currency = null) {
  if (!obj) return { budgetType: null, amount: null, currency, endsAt: null };
  const src = level === 'campaign' ? (obj.budget && obj.budget.level === 'campaign' ? obj.budget : null) : obj;
  const endsAt = level === 'campaign' ? (obj.budget?.endsAt || null) : (obj.endsAt || null);
  const cur = (level === 'campaign' ? obj.budget?.currency || obj.currency : null) || currency || null;
  if (src && src.daily != null) return { budgetType: 'daily', amount: src.daily, currency: cur, endsAt };
  if (src && src.lifetime != null) return { budgetType: 'lifetime', amount: src.lifetime, currency: cur, endsAt };
  return { budgetType: null, amount: null, currency: cur, endsAt };
}

/** Tooltip del presupuesto en un conjunto de una campaña CBO. */
export const META_WRITE_BUDGET_IN_CAMPAIGN_TIP = 'El presupuesto se cambia en la campaña';

/**
 * Ítems de Meta del ⋮ (van primero, antes del vínculo). `[]` = no se muestra nada.
 * - Sin bloque `write` (PROD / bandera apagada) o con un motivo de `META_WRITE_HIDDEN_REASONS` → nada.
 * - Archivado (`reason: archived`): solo «Eliminar» (si `can_delete`) para el dueño.
 * - Quien no es dueño (editor / invitado, o `reason: not_owner`): nada. Mostrar acciones que nunca va a
 *   poder usar no suma (decisión de Ignacio, F2b); si el ⋮ queda vacío, no se renderiza.
 * - Dueño con la cuenta sin habilitar o sin permiso: en gris con tooltip; el clic abre ⚙ (decisión 3).
 * - Dueño habilitado: según los flags. Orden: Pausar / Reactivar, Renombrar, Presupuesto, Fecha de fin,
 *   Archivar (principal) y, aparte y en rojo, Eliminar (`danger`).
 * - Conjunto de una campaña CBO (`budget_level: campaign`): «Cambiar presupuesto» en gris con
 *   «El presupuesto se cambia en la campaña».
 *
 * @param {{ write: object|null, level: 'campaign'|'adset'|'ad', active: boolean, isOwner: boolean,
 *   budgetType?: 'daily'|'lifetime'|null }} input
 * @returns {Array<{ action: string, label: string, muted: boolean, tooltip: string|null, opensSettings: boolean, danger: boolean }>}
 */
export function metaWriteMenuModel({ write, level, active, isOwner, budgetType = null }) {
  if (!write) return [];
  const { reason } = write;
  const item = (action, extra = {}) => ({
    action, label: metaWriteActionLabel(level, action), muted: false, tooltip: null, opensSettings: false,
    // En gris (dueño sin habilitar) Eliminar no va en rojo: todavía no se puede usar.
    danger: action === 'delete' && !extra.muted, ...extra,
  });
  // Solo el dueño ve acciones de escritura (ni el ítem, ni el tooltip «Solo el dueño…»).
  if (!isOwner || reason === 'not_owner') return [];
  if (reason === 'archived') return write.canDelete ? [item('delete')] : [];
  if (reason && META_WRITE_HIDDEN_REASONS.includes(reason)) return [];
  const toggleAction = active ? 'pause' : 'resume';
  const budgetAction = budgetType === 'daily' ? 'set_daily_budget' : budgetType === 'lifetime' ? 'set_lifetime_budget' : null;
  const hasLevelExtras = level === 'campaign' || level === 'adset';
  if (META_WRITE_SETTINGS_REASONS.includes(reason)) {
    // Dueño sin habilitar / sin permiso: en gris lo que se podría hacer acá (el presupuesto, solo
    // donde vive); el clic abre ⚙.
    const actions = [
      toggleAction, 'rename',
      hasLevelExtras && budgetAction && write.budgetLevel === level ? budgetAction : null,
      hasLevelExtras ? 'set_end_time' : null,
      'archive', 'delete',
    ].filter(Boolean);
    const tooltip = reason === 'scope_missing' ? META_WRITE_SCOPE_TIP : META_WRITE_NOT_ENABLED_TIP;
    return actions.map((action) => item(action, { muted: true, tooltip, opensSettings: true }));
  }
  const items = [];
  if (write.canPause) items.push(item('pause'));
  else if (write.canResume) items.push(item('resume'));
  if (write.canRename) items.push(item('rename'));
  if (hasLevelExtras) {
    if (write.canEditBudget && budgetAction) items.push(item(budgetAction));
    else if (level === 'adset' && write.budgetLevel === 'campaign' && (write.canEditEndTime || write.canRename)) {
      items.push(item('budget', { muted: true, tooltip: META_WRITE_BUDGET_IN_CAMPAIGN_TIP }));
    }
    if (write.canEditEndTime) items.push(item('set_end_time'));
  }
  if (write.canArchive) items.push(item('archive'));
  if (write.canDelete) items.push(item('delete'));
  return items;
}

const STATUS_LABELS = { ACTIVE: 'Activa', PAUSED: 'En pausa', ARCHIVED: 'Archivada', DELETED: 'Eliminada' };
/** Estado de Meta (`status`) en castellano para la vista previa. */
export function metaWriteStatusLabel(status, level = 'campaign') {
  const key = String(status || '').toUpperCase();
  if (key === 'ACTIVE') return level === 'ad' ? 'Activo' : 'Activa';
  if (key === 'PAUSED') return 'En pausa';
  if (key === 'ARCHIVED') return level === 'ad' ? 'Archivado' : 'Archivada';
  if (key === 'DELETED') return level === 'ad' ? 'Eliminado' : 'Eliminada';
  return STATUS_LABELS[key] || (status ? String(status) : '—');
}

const pickData = (res) => (res?.data && typeof res.data === 'object' ? res.data : res || {});

const num = (v) => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Vista previa (`preview`) de un dry_run o de un STALE (T6 + T7 + T8, §4). */
export function readMetaWritePreview(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const side = (s) => (s && typeof s === 'object'
    ? { status: str(s.status), name: str(stripMetaTerrioMark(s.name)), endTime: str(s.end_time) }
    : { status: null, name: null, endTime: null });
  return {
    action: str(raw.action),
    level: str(raw.level),
    text: str(raw.text),
    before: side(raw.before),
    after: side(raw.after),
    // Montos y fechas ya formateados por la API (fecha en la zona de la cuenta).
    beforeText: str(raw.before_text),
    afterText: str(raw.after_text),
    currency: str(raw.currency)?.toUpperCase() || null,
    budgetType: str(raw.budget_type),
    maxAllowed: num(raw.max_allowed),
    increase: bool(raw.increase),
    changePct: num(raw.change_pct),
    metaField: str(raw.meta_field),
    timezoneName: str(raw.timezone_name),
    // Conjuntos con fecha de fin propia (aviso ADSETS_OWN_END_TIME).
    adsets: (Array.isArray(raw.adsets) ? raw.adsets : [])
      .map((a) => str(a?.name) || (typeof a === 'string' ? str(a) : null))
      .filter(Boolean),
    warnings: (Array.isArray(raw.warnings) ? raw.warnings : [])
      .map((w) => ({ code: str(w?.code) || 'INFO', text: str(w?.text) }))
      .filter((w) => w.text),
    requiresDoubleConfirm: bool(raw.requires_double_confirm),
    requiresTypedConfirm: str(raw.requires_typed_confirm),
    irreversible: bool(raw.irreversible),
    realMoney: bool(raw.real_money),
  };
}

/**
 * Respuesta del dry_run (`data`): `{ preview, expected, noChange, validated, metaUrl }`.
 */
export function readMetaWriteDryRun(res) {
  const data = pickData(res);
  return {
    preview: readMetaWritePreview(data.preview),
    expected: data.expected && typeof data.expected === 'object' ? data.expected : null,
    noChange: bool(data.no_change),
    metaUrl: str(data.meta_url),
  };
}

/** Respuesta de una acción confirmada (`data`, §4 punto 5). */
export function readMetaWriteResult(res) {
  const data = pickData(res);
  return {
    applied: bool(data.applied),
    noChange: bool(data.no_change),
    replayed: bool(data.replayed),
    action: str(data.action),
    level: str(data.level),
    objectId: str(data.object_id == null ? '' : String(data.object_id)),
    campaignId: str(data.campaign_id == null ? '' : String(data.campaign_id)),
    after: data.after && typeof data.after === 'object' ? data.after : null,
    refreshed: data.refreshed !== false,
    refreshNote: str(data.refresh_note),
    object: data.object && typeof data.object === 'object' ? data.object : null,
    campaign: data.campaign && typeof data.campaign === 'object' ? data.campaign : null,
    metaUrl: str(data.meta_url),
  };
}

/**
 * Error de una acción (dry_run o confirmación) → qué muestra el diálogo.
 * `kind`: stale | rate_limited | unconfirmed | permission | not_enabled | in_progress |
 * key_reused | owner_only | disabled | blocked | not_allowed | not_found | validation | network | generic.
 */
export function readMetaWriteError(res) {
  const code = readMetaErrorCode(res);
  const data = res?.data && typeof res.data === 'object' ? res.data : {};
  const field = (k) => (res?.[k] !== undefined ? res[k] : data[k]);
  const apiText = str(res?.error) || str(data.error);
  const base = {
    code, message: apiText || getMetaErrorMessage(res), metaUrl: str(field('meta_url')), status: res?._httpStatus ?? null,
  };
  switch (code) {
    case 'META_WRITE_STALE':
      return {
        ...base,
        kind: 'stale',
        message: 'Cambió en Meta. Revisá y confirmá de nuevo.',
        preview: readMetaWritePreview(field('preview')),
        expected: field('expected') && typeof field('expected') === 'object' ? field('expected') : null,
      };
    case 'META_RATE_LIMITED':
      return { ...base, kind: 'rate_limited', message: META_WRITE_RATE_LIMITED_TITLE, retryAfterS: readMetaRetryAfterSeconds(res, 60) };
    case 'META_WRITE_BUDGET_LIMIT':
      return { ...base, kind: 'rate_limited', message: META_WRITE_BUDGET_LIMIT_TITLE, retryAfterS: readMetaRetryAfterSeconds(res, 60) };
    case 'META_BUDGET_LEVEL_MISMATCH': {
      const lvl = str(field('budget_level'));
      let message = 'No tiene presupuesto en Meta.';
      if (lvl === 'campaign') message = 'El presupuesto se cambia en la campaña.';
      else if (lvl === 'adset') message = 'El presupuesto se cambia en cada conjunto.';
      return { ...base, kind: 'budget_level', message, budgetLevel: lvl };
    }
    case 'META_WRITE_BUDGET_INVALID':
      return {
        ...base,
        kind: 'budget_invalid',
        reason: str(field('reason')),
        field: str(field('field')),
        maxAllowed: num(field('max_allowed')),
        minAllowed: num(field('min_allowed')),
      };
    case 'META_WRITE_UNCONFIRMED':
      return { ...base, kind: 'unconfirmed', message: META_WRITE_UNCONFIRMED_MESSAGE };
    case 'META_PERMISSION_MISSING':
      return { ...base, kind: 'permission', message: 'Falta el permiso de Meta.' };
    case 'META_WRITE_NOT_ENABLED':
      return { ...base, kind: 'not_enabled', message: 'Esta cuenta no está habilitada.' };
    case 'META_WRITE_IN_PROGRESS':
      return { ...base, kind: 'in_progress', message: 'Hay un cambio en curso. Probá en un momento.' };
    case 'META_WRITE_KEY_REUSED':
      return { ...base, kind: 'key_reused', previousStatus: str(field('previous_status')) };
    case 'META_OWNER_ONLY':
      return { ...base, kind: 'owner_only', message: `${META_WRITE_OWNER_ONLY_TIP}.` };
    case 'META_WRITE_DISABLED':
      return { ...base, kind: 'disabled', message: META_WRITE_KILL_SWITCH_MESSAGE };
    case 'META_WRITE_ACCOUNT_BLOCKED':
      return { ...base, kind: 'blocked', message: 'Esta cuenta no se puede modificar desde Terrio.' };
    case 'META_WRITE_NOT_ALLOWED':
      return { ...base, kind: 'not_allowed' };
    case 'META_CAMPAIGN_NOT_FOUND':
    case 'META_ADSET_NOT_FOUND':
    case 'META_AD_NOT_FOUND':
      return { ...base, kind: 'not_found', message: getMetaErrorMessage(res) };
    case 'VALIDATION_ERROR': {
      const f = str(field('field'));
      if (f === 'confirm_text') return { ...base, kind: 'validation', field: f, message: 'Escribí ELIMINAR para confirmar.' };
      return { ...base, kind: 'validation', field: f };
    }
    default:
      if (res?._httpStatus == null) return { ...base, kind: 'network', message: getMetaErrorMessage(res) };
      return { ...base, kind: 'generic' };
  }
}

/**
 * ¿El preview que falló se puede volver a pedir tal cual? Red / timeout, 5xx o 408 sin `code` de
 * negocio, límite de Meta (después de la cuenta regresiva) o un cambio en curso. Los definitivos
 * (403, validación, prod_locked / kill switch, cuenta bloqueada…) no: siguen como estaban.
 */
export function isMetaWriteRetryableError(err) {
  if (!err) return false;
  if (['network', 'rate_limited', 'in_progress'].includes(err.kind)) return true;
  return err.kind === 'generic' && (err.status === 408 || (err.status >= 500 && err.status <= 599));
}

/** uuid v4 para `idempotency_key` (nuevo por cada confirmación). */
export function newMetaIdempotencyKey() {
  const c = typeof window !== 'undefined' ? window.crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // eslint-disable-line no-bitwise
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // eslint-disable-line no-bitwise
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Nombre nuevo válido para `rename` (1 a 400, sin saltos de línea ni caracteres de control). */
export const META_RENAME_MAX = 400;
export function validateMetaRename(value) {
  const v = typeof value === 'string' ? value.trim() : '';
  // eslint-disable-next-line no-control-regex
  if (!v || v.length > META_RENAME_MAX || /[\u0000-\u001f\u007f]/.test(v)) {
    return `Escribí un nombre de hasta ${META_RENAME_MAX} caracteres.`;
  }
  return '';
}

/**
 * Monto escrito por el usuario → texto para `value` (unidad mayor, punto decimal, hasta 2 decimales).
 * Acepta «4.500,50», «4500,5» o «4500.50». `null` = inválido.
 */
export function parseMetaBudgetInput(value) {
  let v = String(value ?? '').replace(/\s|\$/g, '');
  if (!v) return null;
  if (v.includes(',')) v = v.replace(/\./g, '').replace(',', '.');
  else if (/^\d{1,3}(\.\d{3})+$/.test(v)) v = v.replace(/\./g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(v)) return null;
  if (Number(v) <= 0) return null;
  return v.replace(/^0+(?=\d)/, '');
}
export function validateMetaBudget(value) {
  return parseMetaBudgetInput(value) == null ? 'Escribí un monto mayor a 0.' : '';
}

/** Fecha de fin escrita (`YYYY-MM-DD`). */
export function validateMetaEndDate(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || '')) ? '' : 'Elegí una fecha.';
}

/** Monto actual como texto editable («4500» o «4500,50»). */
export function metaBudgetInputValue(amount) {
  if (amount == null || !Number.isFinite(Number(amount))) return '';
  const n = Number(amount);
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace('.', ',');
}

// Lo que se toma de la respuesta (decisión 6): estado, nombre, `write` y la entrega en cascada.
// Las métricas quedan como estaban (la respuesta viene con 30 días): las corrige el reload silencioso.
const STATE_KEYS = ['name', 'effectiveStatus', 'statusLabel', 'delivery', 'isActiveInMeta', 'isActive', 'write'];
const CAMPAIGN_STATE_KEYS = [...STATE_KEYS, 'isDeliveringNow', 'activeAdsCount'];
const pickState = (from, keys) => keys.reduce((acc, k) => (from[k] === undefined ? acc : { ...acc, [k]: from[k] }), {});

const campaignActiveNow = (c) => (typeof c?.isActive === 'boolean' ? c.isActive : c?.isDeliveringNow === true);

/**
 * Aplica la respuesta de una acción a la lista ya cargada (sin recargar todavía).
 * Con «Activas» (`includeInactive: false`), una campaña que deja de estar activa sale enseguida
 * (decisión 4). Si `campaign` y `object` vienen en null, no toca nada (el reload lo arregla).
 */
export function applyMetaWriteResult(campaigns, result, { includeInactive = false } = {}) {
  if (!Array.isArray(campaigns) || !result) return campaigns;
  const fresh = result.campaign ? normalizeMetaCampaign(result.campaign) : null;
  const freshAd = result.level === 'ad' && result.object
    ? normalizeMetaAd(result.object)
    : null;
  const campaignId = fresh?.id || result.campaignId;
  if (!campaignId || (!fresh && !freshAd)) return campaigns;
  const next = campaigns.map((c) => {
    if (c.id !== campaignId) return c;
    const updated = fresh ? { ...c, ...pickState(fresh, CAMPAIGN_STATE_KEYS) } : { ...c };
    const freshAds = new Map((fresh?.ads || []).map((a) => [a.id, a]));
    if (freshAd) freshAds.set(freshAd.id, { ...(freshAds.get(freshAd.id) || {}), ...freshAd });
    updated.ads = c.ads.map((a) => (freshAds.has(a.id) ? { ...a, ...pickState(freshAds.get(a.id), STATE_KEYS) } : a));
    return updated;
  });
  // Eliminada (temporal): sale enseguida también en «Todas» (el Front no muestra eliminadas).
  return next.filter((c) => c.id !== campaignId
    || (!isDeletedMetaCampaign(c) && (includeInactive || campaignActiveNow(c))));
}

/** Aviso de éxito (toast). `replayed` es un éxito normal. */
export function metaWriteSuccessMessage(level, action, result) {
  const { Noun, the, o } = nounOf(level);
  if (result?.noChange) {
    if (action === 'pause') return `${the} ya estaba en pausa.`;
    if (action === 'resume') return `${the} ya estaba activ${o}.`;
    if (META_BUDGET_ACTIONS.includes(action)) return 'Ya tenía ese presupuesto.';
    if (action === 'set_end_time') return 'Ya tenía esa fecha de fin.';
    if (action === 'archive') return `${the} ya estaba archivad${o}.`;
    if (action === 'delete') return `${the} ya estaba eliminad${o}.`;
    return 'Ya tenía ese nombre.';
  }
  if (action === 'pause') return `${Noun} pausad${o}.`;
  if (action === 'resume') return `${Noun} reactivad${o}.`;
  if (META_BUDGET_ACTIONS.includes(action)) return 'Presupuesto actualizado.';
  if (action === 'set_end_time') return 'Fecha de fin actualizada.';
  if (action === 'archive') return `${Noun} archivad${o}.`;
  if (action === 'delete') return `${Noun} eliminad${o}.`;
  return `${Noun} renombrad${o}.`;
}
